import React, { useEffect } from 'react';
import { ChevronRight, ArrowLeft } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import TryoutSchedule from '../components/TryoutSchedule';

function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleRegisterClick = () => {
    navigate('/parent');
  };

  // Native fragment scrolling fires before TryoutSchedule's async data loads,
  // so the section moves out from under the scroll position. Re-scroll on
  // layout changes until the user takes over.
  useEffect(() => {
    if (location.hash !== '#tryouts') return;

    const el = document.getElementById('tryouts');
    if (!el) return;

    let userInteracted = false;
    const markInteracted = () => { userInteracted = true; };
    window.addEventListener('wheel', markInteracted, { passive: true });
    window.addEventListener('touchmove', markInteracted, { passive: true });
    window.addEventListener('keydown', markInteracted);

    const scrollIfNeeded = () => {
      if (userInteracted) return;
      el.scrollIntoView({ block: 'start' });
    };

    scrollIfNeeded();
    const observer = new ResizeObserver(scrollIfNeeded);
    observer.observe(el);
    const stopTimer = window.setTimeout(() => observer.disconnect(), 3000);

    return () => {
      observer.disconnect();
      window.clearTimeout(stopTimer);
      window.removeEventListener('wheel', markInteracted);
      window.removeEventListener('touchmove', markInteracted);
      window.removeEventListener('keydown', markInteracted);
    };
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div
        className="relative h-[60vh] bg-cover bg-center"
        style={{
          backgroundImage: 'url("/cdo_field_logo.png")',
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50" />
        <a
          href="https://cdosoccer.com"
          className="absolute top-4 left-4 z-10 inline-flex items-center bg-white bg-opacity-90 text-gray-800 px-4 py-2 rounded-full font-semibold text-sm hover:bg-opacity-100 transition-colors shadow-md"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to CDO Website
        </a>
        <div className="relative h-full flex items-center justify-center">
          <div className="text-center text-white px-4">
            <h1 className="text-5xl font-bold mb-4">CDO Soccer Alliance</h1>
            <h2 className="text-3xl font-semibold mb-6">2026-27 Club Placements</h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#tryouts"
                className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-blue-700 transition-colors"
              >
                View Schedule
                <ChevronRight className="w-5 h-5 ml-2" />
              </a>
              <button
                onClick={handleRegisterClick}
                className="inline-flex items-center bg-green-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-green-700 transition-colors"
              >
                Register for Placements
                <ChevronRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Placement Schedule</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Join one of the most prestigious youth soccer clubs in the North Tucson area. Our comprehensive placement process ensures
            proper team placement for optimal player development and enjoyment.
          </p>
        </div>

        {/* Competition Levels */}
        <div className="mb-12 grid md:grid-cols-3 gap-8">
          <div className="bg-blue-50 rounded-lg shadow-md overflow-hidden">
            <div className="bg-blue-600 py-3 px-6">
              <h3 className="text-2xl font-bold text-white">PREMIER LEVEL</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 leading-relaxed">
                CDO Premier has been formed to provide the most talented and committed soccer players in Pima County 
                to play at the highest level possible. Teams are made up of players from Tucson, Marana, Oro Valley and beyond. 
                Through quality training and high level competition we aim to develop our soccer players both on and off the field 
                to prepare them for high school, college and further.
              </p>
            </div>
          </div>
          
          <div className="bg-green-50 rounded-lg shadow-md overflow-hidden">
            <div className="bg-green-600 py-3 px-6">
              <h3 className="text-2xl font-bold text-white">ELITE LEVEL</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 leading-relaxed">
                Behind our Premier Teams we have our Elite Team program. At CDO we acknowledge that some players develop quicker than others, 
                some need a slower trajectory and some need to have a slightly less intense environment.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                This is where our Elite Level comes in. Players still get to work with high level coaches, working under the same curriculum 
                and ideals as the Premier Level program as we look to help players progress and move up to the Premier Level when they are ready.
              </p>
            </div>
          </div>
          
          <div className="bg-amber-50 rounded-lg shadow-md overflow-hidden">
            <div className="bg-amber-600 py-3 px-6">
              <h3 className="text-2xl font-bold text-white">ALLIANCE LEVEL</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 leading-relaxed">
                CDO Alliance teams consist of players with less experience in the game than those on Premier and Elite groups. However, the expectation of Alliance players is that they still remain committed to their team and strive to be the best player they can possibly be. Alliance teams typically play in less tournaments and compete in less challenging divisions of local leagues.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Therefore, Alliance team players looking for a greater challenge are encouraged to accept opportunities to play for the Elite teams when those offers become available in order to gain experience playing at that level.
              </p>
            </div>
          </div>
        </div>

        {/* Register CTA */}
        <div className="text-center mb-12">
          <button
            onClick={handleRegisterClick}
            className="inline-flex items-center bg-green-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-green-700 transition-colors shadow-md"
          >
            Register for Placements
            <ChevronRight className="w-6 h-6 ml-2" />
          </button>
        </div>

        <div id="tryouts">
          <TryoutSchedule />
        </div>

        {/* Additional Information */}
        <div className="mt-16 bg-white rounded-lg shadow-lg p-8">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">What to Bring</h3>
          <ul className="list-disc list-inside space-y-2 text-gray-600">
            <li>Cleats and shin guards</li>
            <li>Water bottle</li>
            <li>Soccer ball (size appropriate for age group)</li>
          </ul>
          
          <div className="mt-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Registration Process</h3>
            <p className="text-gray-600 mb-6">
              Pre-registration is required for all placements. Please arrive 15 minutes before your scheduled time for
              check-in. A parent or guardian must be present.
            </p>
            <button
              onClick={handleRegisterClick}
              className="inline-flex items-center bg-green-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-green-700 transition-colors"
            >
              Register Now
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

export default LandingPage;